export const federalStateNames = [
  'Baden-Württemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen'
] as const

export type FederalStateName = typeof federalStateNames[number]

export interface Configs {
  [key: string]: Config
}

export interface Config {
  id: number
  locale: string
  alias: string
  fields: Array<ConfigField>
  federal_states: Array<FederalStateConfig>
  factors: FactorsConfig
  insurance_bonus: Array<InsuranceBonus>
  social_insurance: SocialInsuranceConfig
  taxes: {
    child_allowance: ChildAllowanceConfig
    vat: number
  }
}

export interface ConfigField {
  name: string
  default: number | boolean | string
  min: number | null
  max: number | null
  items: Array<any>
  read_only?: boolean
  hide?: boolean
}

export interface InsuranceBonus {
  price_class: number
  bonus: number
}

export type ChildAllowanceConfig = {
  subsistence_level: number
  care_needs: number
}

export interface CustomFactors {
  leasing_factor: number
  expected_used_purchase_price_factor: number
}

export type CustomFieldConfig = Partial<Omit<ConfigField, 'name' | 'items'>> & {
  default?: string | number | boolean
}

export type CustomConfig = {
  purchase_price?: CustomFieldConfig
  uvp_price?: CustomFieldConfig
  monthly_salary?: CustomFieldConfig
  tax_class?: CustomFieldConfig
  employer_subsidy?: CustomFieldConfig
  employer_tax_deduction?: CustomFieldConfig
  assumption_of_insurance_costs?: CustomFieldConfig
  federal_state?: CustomFieldConfig
  child_allowance?: CustomFieldConfig
  health_insurance?: CustomFieldConfig
  health_insurance_additional_contribution?: CustomFieldConfig
  church_tax?: CustomFieldConfig
  pension_insurance?: CustomFieldConfig
  factors?: CustomFactors
} & {
  [key: string]: CustomFieldConfig | CustomFactors | undefined
}
export type FederalStateConfig = {
  name: FederalStateName
} & FederalStateProperties

export type FederalStateProperties = {
  church_tax: number
  care_insurance: CareInsuranceConfig
  pension_insurance: InsuranceLimit
  unemployment_insurance: InsuranceLimit
}

export interface InsuranceLimit {
  contribution_assessment_limit: number // Beitragsbemessungsgrenzen
}

type CareInsuranceEmployeeSteps = '0' | '0.5' | '1' | '1.5' | '2' | '2.5'

export type CareInsuranceConfig = {
  employer_value: number
  employee_values: Record<CareInsuranceEmployeeSteps, number>
  employee_without_children: number
} & InsuranceLimit

type FactorsConfig = {
  leasing_factor: number
  duration: number
  expected_used_purchase_price_factor: number
}

type SimpleEmployeeProperties = {
  employer_value: number
  employee_value: number
}

/**
 * Werte für Sozialversicherung
 */
type SocialInsuranceConfig = {
  pension_insurance: SimpleEmployeeProperties
  unemployment_insurance: SimpleEmployeeProperties
  health_insurance: {
    employer_value: number
    employee_value: number
    contribution_assessment_limit: number
  }
  care_insurance: {
    employer_value: number
    employee_values: Record<CareInsuranceEmployeeSteps, number>
    employee_without_children: number
    contribution_assessment_limit: number
  }
}
